<!--
  File name: PasswordForm.vue
  Program Description:
    - This component displays the generic password form for the system.
    - Used in the password reset and change password pages
-->

<template>
  <Card
    :tile="cardTile"
    :flat="cardFlat"
    :show-title="!!title"
  >
    <template v-slot:title>
      {{ title }}
    </template>
    <template v-slot:content>
      <v-card-subtitle class="py-5 px-0 mx-0  text-subtitle-2">
        <p class="black--text">
          Password must contain:
        </p>
        <p
          :class="validateLength"
          class="my-1"
        >
          At least 12 characters
        </p>
        <p
          :class="validateNumeric"
          class="my-1"
        >
          At least 1 number (0 to 9)
        </p>
        <p
          :class="validateTextCase"
          class="my-1"
        >
          At least 1 upper case letter or lower case letter (a to z or A to Z)
        </p>
        <p
          :class="validateSpecial"
          class="my-1"
        >
          At least 1 special character (!@#$%^&*.,)
        </p>
      </v-card-subtitle>
      <v-text-field
        v-if="oldPassword"
        id="currentPassword"
        label="Current Password"
        :outlined="canEdit"
        :readonly="!canEdit || loading"
        :value="currentPassword"
        :error-messages="genericError('currentPassword', 'Current Password')"
        :append-icon="$data.$opts.password.showCurrent ? 'mdi-eye' : 'mdi-eye-off'"
        :type="$data.$opts.password.showCurrent ? 'text' : 'password'"
        class="required-field"
        @keyup="colorValidate()"
        @input="handler('currentPassword', $event)"
        @blur="$v.currentPassword.$touch()"
        @change="handler('currentPassword', $event.trim())"
        @click:append="$data.$opts.password.showCurrent = !$data.$opts.password.showCurrent"
      />
      <v-text-field
        id="password"
        :label="passwordLabel"
        :outlined="canEdit"
        :readonly="!canEdit || loading"
        :value="password"
        :error-messages="passwordError('password')"
        :append-icon="$data.$opts.password.show ? 'mdi-eye' : 'mdi-eye-off'"
        :type="$data.$opts.password.show ? 'text' : 'password'"
        class="required-field"
        @keyup="colorValidate()"
        @input="handler('password', $event)"
        @blur="$v.password.$touch()"
        @change="handler('password', $event.trim())"
        @click:append="$data.$opts.password.show = !$data.$opts.password.show"
      />
      <v-text-field
        id="confirmPassword"
        label="Confirm Password"
        :outlined="canEdit"
        :readonly="!canEdit || loading"
        :value="confirmPassword"
        :error-messages="confirmPasswordError"
        :append-icon="$data.$opts.password.showConfirm ? 'mdi-eye' : 'mdi-eye-off'"
        :type="$data.$opts.password.showConfirm ? 'text' : 'password'"
        class="required-field"
        @input="handler('confirmPassword', $event)"
        @blur="$v.confirmPassword.$touch()"
        @change="handler('confirmPassword', $event.trim())"
        @click:append="$data.$opts.password.showConfirm = !$data.$opts.password.showConfirm"
      />
    </template>
    <template v-slot:action>
      <v-btn
        v-if="!isResetPassword"
        id="update-password-btn"
        color="primary"
        type="button"
        block
        class="py-6 my-n2"
        :loading="$data.$meta.isLoading"
        @click="resetPassword"
      >
        Update Password
      </v-btn>
      <v-col
        v-if="isResetPassword"
        class="pa-0 d-flex justify-end align-center"
      >
        <v-btn
          id="update-password-btn"
          color="primary"
          type="button"
          class="py-5 px-5 my-n2"
          :loading="$data.$meta.isLoading"
          @click="submit"
        >
          Save Changes
        </v-btn>
      </v-col>
    </template>
  </Card>
</template>

<script>
import { custom, validationErrors, validationMixin } from '@/utils/validations'
import Card from '@/components/Card'
import { sameAs } from 'vuelidate/lib/validators'
import password from '@/utils/webapi/password'
import { errors } from '@/meta'

export default {
  name: 'PasswordForm',
  components: {
    Card
  },
  mixins: [validationMixin],
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    isResetPassword: {
      type: Boolean,
      default: false
    },
    oldPassword: {
      type: Boolean,
      default: false
    },
    loading: Boolean,
    password: { type: String, default: undefined },
    confirmPassword: { type: String, default: undefined },
    currentPassword: { type: String, default: undefined },
    cardFlat: {
      type: Boolean,
      default: true
    },
    cardTile: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    $opts: {
      password: {
        show: false,
        showConfirm: false,
        showCurrent: false
      },
      validation: {
        length: false,
        number: false,
        textCase: false,
        special: false
      }
    },
    $meta: {
      isLoading: false
    },
    isOtpInvalid: false
  }),
  computed: {
    ...validationErrors,
    validateLength () {
      return this.$data.$opts.validation.length ? 'text-success' : ''
    },
    validateNumeric () {
      return this.$data.$opts.validation.number ? 'text-success' : ''
    },
    validateTextCase () {
      return this.$data.$opts.validation.textCase ? 'text-success' : ''
    },
    validateSpecial () {
      return this.$data.$opts.validation.special ? 'text-success' : ''
    },
    passwordLabel () {
      return this.$props.isResetPassword ? 'New Password' : 'Password'
    },
    isGuest () {
      return this.$s.userRole.includes('guest')
    }
  },
  validations: {
    currentPassword: custom.currentPassword,
    password: custom.password,
    confirmPassword: {
      sameAs: sameAs('password')
    }
  },
  async created () {
    this.$emit('validation', this.$v)
    await this.checkOtp()
  },
  methods: {
    colorValidate () {
      const validation = this.$data.$opts.validation
      const password = this.$props.password
      validation.length = (password.length >= 12)
      validation.number = /(?=.*?[0-9])/.test(password)
      validation.textCase = /(?=.*?[A-Z])(?=.*?[a-z])/.test(password)
      validation.special = /(?=.*?[!@#$%^&*.,])/.test(password)
    },
    async submit () {
      this.$v.password.$touch()
      this.$v.currentPassword.$touch()
      this.$v.confirmPassword.$touch()
      if (!this.$v.$invalid) {
        this.$data.$meta.isLoading = true
        const resp = await password.request.change(this.currentPassword)
        const jData = await resp.json()
        if (resp.ok) {
          this.finishChangePass(jData.otp)
        } else if (jData.error === 'Incorrect password.') {
          this.$store.dispatch('showAlert', {
            message: 'Old password is incorrect. Please input correct password.',
            type: 'error'
          })
          this.$data.$meta.isLoading = false
        } else if (jData.error === 'You already reached the maximum limit of requesting password for this month.') {
          this.$store.dispatch('showAlert', {
            message: jData.error,
            type: 'error'
          })
          this.$data.$meta.isLoading = false
        } else {
          this.$store.dispatch('showAlert', {
            message: errors.generic,
            type: 'error'
          })
          this.$data.$meta.isLoading = false
        }
      }
      if (!this.$v.password.$invalid && this.$v.confirmPassword.$invalid) {
        this.$store.dispatch('showAlert', {
          message: 'Confirm your new password.',
          type: 'error'
        })
      }
    },
    async finishChangePass (otp) {
      this.$data.$meta.isLoading = true
      const resp = await password.change(otp, this.password)
      resp && (this.$data.$meta.isLoading = false)
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: 'Your password has been changed. Use your new password to login.',
          type: 'success'
        })
        this.$s.destroySession()
      } else {
        this.$store.dispatch('showAlert', {
          message: errors.generic,
          type: 'error'
        })
        this.$data.$alert.show = true
      }
    },
    async resetPassword () {
      if (this.isOtpInvalid) {
        return this.$store.dispatch('showAlert', {
          message: 'Password Reset Link already expired.',
          type: 'error'
        })
      }

      this.$v.password.$touch()
      this.$v.confirmPassword.$touch()
      if (!this.$v.password.$invalid && !this.$v.confirmPassword.$invalid) {
        this.$data.$meta.isLoading = true
        const { query } = this.$route
        const resp = await password.emailChangePassword(query.username, query.otp, this.password)
        const jData = await resp.json()
        this.$data.$meta.isLoading = false
        if (resp.ok) {
          this.$store.dispatch('showAlert', {
            message: 'Your password has been changed successfully! Use your new password to Login.',
            type: 'success'
          })
          this.$router.push({ name: 'Login' })
        } else {
          const message = 'Password Reset Link already expired.'
          const error = this.isOtpInvalid ? message : jData.error || jData.message
          this.$store.dispatch('showAlert', {
            message: error,
            type: 'error'
          })
        }
      }
      if (!this.$v.password.$invalid && this.$v.confirmPassword.$invalid) {
        this.$store.dispatch('showAlert', {
          message: 'Confirm your new password.',
          type: 'error'
        })
      }
    },
    async checkOtp () {
      await this.$s.ready
      if (!this.isGuest) return
      const { query } = this.$route
      const resp = await password.emailChangePassword(query.username, query.otp, this.password)
      const jData = await resp.json()
      if (jData.error === 'OTP is expired.' || jData.error === 'OTP is invalid.' || jData.error === 'OTP has already been used.') {
        this.isOtpInvalid = true
        this.$store.dispatch('showAlert', {
          message: 'Password Reset Link already expired.',
          type: 'error'
        })
      } else this.isOtpInvalid = false
    },
    handler (key, value) {
      this.$emit('change', { key, value })
      this.$v[`${key}`] && this.$v[`${key}`].$touch()
    }
  }
}
</script>

<style scoped>
  .v-icon--link {
    pointer-events: auto;
  }

  .v-input >>> input[type=number]::-webkit-outer-spin-button,
  .v-input >>> input[type=number]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .v-card >>> .v-card__text {
    padding:0
  }
  .v-input >>> input[type=number] {
    -moz-appearance: textfield;
  }
  .text-success{
    color: green;
  }
  .text-danger{
    color: red;
  }
</style>
